/**
 * Typed wrappers around the composers REST API (Task 27).
 *
 * Endpoints (mounted at `/api/v1/admin/composers` server-side; the
 * `apiGet`/`apiPost`/... helpers transparently prepend `/api`, so we
 * pass `/v1/...`):
 *
 *   GET   /v1/admin/composers
 *   GET   /v1/admin/composers/:id
 *   POST  /v1/admin/composers
 *   PUT   /v1/admin/composers/:id/programs
 *   POST  /v1/admin/composers/:id/{submit-for-review|recommend-approve|approve|reject|revoke|reattest}
 *
 * Task 30 consolidated all i18n namespace registration into `i18n/i18n.ts`
 * (auto-discovers `locales/{lang}/{ns}.json` via `import.meta.glob`), so no
 * `addResourceBundle` call is needed here.
 */

import type {
  CapabilityMatrix,
  ComposerKind,
  ComposerSummary,
  NameI18n,
  ProgramItemBinding,
  ProgramSummary,
  ProgramUpsert,
  ReplaceZoneItemsInput,
  ResourceState,
  Zone,
} from '@displaycms/contracts';

import { apiGet, apiPatch, apiPost, ApiError } from '@/api/client';

// ---------------------------------------------------------------------------
// Layout constants
// ---------------------------------------------------------------------------
//
// Phase 1 uses a single "singleton" full-screen layout for every program.
// The UUID below is hard-coded; multi-zone editing arrives in Phase 2.

export const SINGLETON_LAYOUT_ID = '00000000-0000-4000-8000-000000000001';
export const FULL_ZONE_ID = 'full';

// ---------------------------------------------------------------------------
// API types
// ---------------------------------------------------------------------------

export interface ComposerListResponse {
  items: ComposerSummary[];
  total: number;
  page: number;
  per_page: number;
}

/**
 * Detail response shape: a `ComposerSummary` plus its current programs.
 *
 * NOTE on server shape: the server (`ComposerDetailDto`) returns programs
 * nested under `current_version.programs`, not at the top level. The
 * `getComposer` and `activateComposerVersion` functions transform the raw
 * server response to this flat shape for backward compatibility with the
 * detail and layout-editor pages.
 */
export interface ComposerDetail extends ComposerSummary {
  programs: ProgramSummary[];
  current_version_number?: number | null;
  // Composition-level background-music track on the current version
  // (approved audio ResourceVersion id), or null when none is set.
  background_audio_resource_version_id?: string | null;
}

/**
 * Raw shape returned by `GET /admin/composers/:id` on the server.
 * Programs are nested under `current_version` — we flatten them in
 * `normaliseComposerDetail` before exposing them to the rest of the admin SPA.
 */
interface RawComposerDetail {
  id: string;
  name_i18n: { fr?: string; en?: string };
  kind: string;
  state: string;
  current_version_id: string | null;
  created_at: string;
  current_version: {
    id: string;
    composer_id: string;
    version_number: number;
    state: string;
    signed_manifest_id: string | null;
    background_audio_resource_version_id?: string | null;
    created_at: string;
    programs: ProgramSummary[];
  } | null;
}

/**
 * Transforms the raw server `ComposerDetailDto` (programs nested under
 * `current_version`) into the flat `ComposerDetail` shape the SPA pages
 * consume (top-level `programs` + `current_version_number`).
 */
function normaliseComposerDetail(raw: RawComposerDetail): ComposerDetail {
  return {
    id: raw.id,
    name_i18n: raw.name_i18n as ComposerSummary['name_i18n'],
    kind: raw.kind as ComposerSummary['kind'],
    state: raw.state as ComposerSummary['state'],
    current_version_id: raw.current_version_id,
    created_at: raw.created_at,
    programs: raw.current_version?.programs ?? [],
    current_version_number: raw.current_version?.version_number ?? null,
    background_audio_resource_version_id:
      raw.current_version?.background_audio_resource_version_id ?? null,
  };
}

export interface ListComposersQuery {
  state?: ResourceState;
  kind?: ComposerKind;
  search?: string;
  page?: number;
  per_page?: number;
}

export interface CreateComposerInput {
  name_i18n: NameI18n;
  kind: ComposerKind;
  // P3 §7 — optional minimum capability flags the player must satisfy to
  // play this composer's initial version (matches the `ComposerCreate`
  // contract field name exactly). null/omitted = the default/universal
  // profile; only threaded onto the request body when provided.
  min_capability_flags?: string[] | null;
}

export interface AuditActionInput {
  rationale?: string;
  mark_permanent?: boolean;
  expires_in_seconds?: number;
}

export type ComposerAuditAction =
  | 'submit_for_review'
  | 'recommend_approve'
  | 'approve'
  | 'reject'
  | 'revoke'
  | 'reattest';

// ---------------------------------------------------------------------------
// List / get
// ---------------------------------------------------------------------------

function buildListQuery(query: ListComposersQuery): string {
  const params = new URLSearchParams();
  if (query.state) params.set('state', query.state);
  if (query.kind) params.set('kind', query.kind);
  if (query.search) params.set('search', query.search);
  if (query.page !== undefined) params.set('page', String(query.page));
  if (query.per_page !== undefined) params.set('per_page', String(query.per_page));
  const s = params.toString();
  return s.length > 0 ? `?${s}` : '';
}

export function listComposers(
  query: ListComposersQuery = {},
  options?: { signal?: AbortSignal },
): Promise<ComposerListResponse> {
  const opts = options?.signal ? { signal: options.signal } : undefined;
  return apiGet<ComposerListResponse>(
    `/v1/admin/composers${buildListQuery(query)}`,
    opts,
  );
}

export async function getComposer(
  id: string,
  options?: { signal?: AbortSignal },
): Promise<ComposerDetail> {
  const opts = options?.signal ? { signal: options.signal } : undefined;
  const raw = await apiGet<RawComposerDetail>(`/v1/admin/composers/${id}`, opts);
  return normaliseComposerDetail(raw);
}

// ---------------------------------------------------------------------------
// Capability matrix (P3 §6.2 / §8.5)
// ---------------------------------------------------------------------------
//
// Server-computed answer to "for this composer, which co-active
// approved+signed version would each assigned player actually be served?".
// Pure read; the `CapabilityMatrix` component renders the result.

export function getCapabilityMatrix(
  composerId: string,
  options?: { signal?: AbortSignal },
): Promise<CapabilityMatrix> {
  const opts = options?.signal ? { signal: options.signal } : undefined;
  return apiGet<CapabilityMatrix>(
    `/v1/admin/composers/${composerId}/capability-matrix`,
    opts,
  );
}

// ---------------------------------------------------------------------------
// Create
// ---------------------------------------------------------------------------

export function createComposer(
  input: CreateComposerInput,
): Promise<ComposerSummary> {
  // Conditional-spread so the `min_capability_flags` key is ABSENT (not
  // `undefined`) when the caller doesn't pass it — keeps the request body
  // byte-identical to the pre-P3 shape and stays `exactOptionalPropertyTypes`
  // safe (mirrors the `apiPutVoid` `...(x ? { x } : {})` idiom below).
  const body = {
    name_i18n: input.name_i18n,
    kind: input.kind,
    ...(input.min_capability_flags !== undefined
      ? { min_capability_flags: input.min_capability_flags }
      : {}),
  };
  return apiPost<ComposerSummary>('/v1/admin/composers', body);
}

// ---------------------------------------------------------------------------
// Import (JSON) — POST /v1/admin/composers/import-json
// ---------------------------------------------------------------------------
//
// Accepts a parsed JSON object whose shape matches what an export would
// produce: `{name_i18n, kind, programs?, min_capability_flags?}`. The
// server validates against ImportComposerJsonSchema and runs the create
// + replacePrograms calls in sequence so partial failure leaves no
// orphan rows. Returns the created composer detail.
export function importComposerJson(
  payload: unknown,
): Promise<ComposerSummary> {
  return apiPost<ComposerSummary>('/v1/admin/composers/import-json', payload);
}

// ---------------------------------------------------------------------------
// Update (metadata) — PATCH /v1/admin/composers/:id
// ---------------------------------------------------------------------------
//
// Accepts `{name_i18n?, kind?}` (at least one required by the server's
// UpdateComposerSchema). Returns the updated composer summary. Used by
// the composer detail page's rename dialog.
export interface UpdateComposerInput {
  name_i18n?: NameI18n;
  kind?: ComposerKind;
}

export function updateComposer(
  id: string,
  patch: UpdateComposerInput,
): Promise<ComposerSummary> {
  return apiPatch<ComposerSummary>(`/v1/admin/composers/${id}`, patch);
}

// ---------------------------------------------------------------------------
// Background music — PATCH /v1/admin/composers/:id/background-audio
// ---------------------------------------------------------------------------
//
// Set (uuid) or clear (null) the composition-level background-music track on
// the composer's current version. The server validates that the track is an
// approved, tenant-owned `audio` resource and that the version is editable;
// it returns the full composer detail (which we normalise to the flat shape).
export function setComposerBackgroundAudio(
  composerId: string,
  resourceVersionId: string | null,
): Promise<ComposerDetail> {
  return apiPatch<RawComposerDetail>(
    `/v1/admin/composers/${composerId}/background-audio`,
    { background_audio_resource_version_id: resourceVersionId },
  ).then(normaliseComposerDetail);
}

// ---------------------------------------------------------------------------
// Activate a version into its capability-profile slot (P3 W2 A3 / DS §2)
// ---------------------------------------------------------------------------
//
// Idempotently (re)activates an ALREADY approved+signed `ComposerVersion`
// into its capability-profile slot. Resolves the W1 capability boundary: a
// pre-P3 composer that later gained a P3 capability-specific active version
// would otherwise leave legacy-flags players on `assignment.unavailable`
// until the default/universal version is explicitly (re)activated.
//
// Server route `POST /v1/admin/composers/:id/versions/:versionId/activate`
// (permission `composers.approve`, same as the approve route) returns the
// composer-GET response shape — i.e. `ComposerDetail`, identical to
// `getComposer`. No body; mirrors `createComposer`'s `apiPost` style.
export async function activateComposerVersion(
  composerId: string,
  versionId: string,
): Promise<ComposerDetail> {
  const raw = await apiPost<RawComposerDetail>(
    `/v1/admin/composers/${composerId}/versions/${versionId}/activate`,
  );
  return normaliseComposerDetail(raw);
}

// ---------------------------------------------------------------------------
// Patch a single program item binding (PATCH — W8 T2)
// ---------------------------------------------------------------------------
//
// PATCH /v1/admin/composers/:id/program/items/:zone_id
// Upserts the binding for one zone on the composer's current version.
// Returns the updated programs list (ProgramSummary[]).

export function patchProgramItem(
  composerId: string,
  zoneId: string,
  binding: ProgramItemBinding,
): Promise<{ programs: ProgramSummary[] }> {
  return apiPatch<{ programs: ProgramSummary[] }>(
    `/v1/admin/composers/${composerId}/program/items/${zoneId}`,
    binding,
  );
}

// ---------------------------------------------------------------------------
// Replace programs (PUT — full replacement of the program list)
// ---------------------------------------------------------------------------

/**
 * PUT helper using `fetch` directly. `apiPut` doesn't exist in
 * `api/client.ts` (Phase 1 omitted it), and that file is outside Task 27's
 * touchable scope. We replicate the cookie/CSRF/header conventions here.
 */
async function apiPutVoid(path: string, body: unknown): Promise<void> {
  const url = path.startsWith('/') ? `/api${path}` : `/api/${path}`;
  let response: Response;
  try {
    response = await fetch(url, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'displaycms-admin',
      },
      body: JSON.stringify(body),
    });
  } catch (cause) {
    throw new ApiError(0, {
      message: 'errors.network',
      code: 'network_error',
      details: cause instanceof Error ? cause.message : String(cause),
    });
  }
  if (response.status === 204) return;
  if (!response.ok) {
    let parsed: { message?: string; code?: string; details?: unknown } = {};
    try {
      parsed = (await response.json()) as typeof parsed;
    } catch {
      // ignore — falls through to generic
    }
    throw new ApiError(response.status, {
      message:
        typeof parsed.message === 'string' && parsed.message.length > 0
          ? parsed.message
          : 'errors.generic',
      ...(typeof parsed.code === 'string' ? { code: parsed.code } : {}),
      ...(parsed.details !== undefined ? { details: parsed.details } : {}),
    });
  }
}

export function replacePrograms(
  composerId: string,
  programs: ProgramUpsert[],
): Promise<void> {
  return apiPutVoid(`/v1/admin/composers/${composerId}/programs`, {
    programs,
  });
}

// ---------------------------------------------------------------------------
// Replace all items for a zone (Feature #8 — multi-item playlist editor)
// ---------------------------------------------------------------------------
//
// PUT /v1/admin/composers/:id/program/items/:zone_id/playlist
// Accepts a bare JSON array of ProgramItemBinding; returns the updated
// programs list. Empty array clears all items for the zone.

async function apiPutJson<T>(path: string, body: unknown): Promise<T> {
  const url = path.startsWith('/') ? `/api${path}` : `/api/${path}`;
  let response: Response;
  try {
    response = await fetch(url, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'displaycms-admin',
      },
      body: JSON.stringify(body),
    });
  } catch (cause) {
    throw new ApiError(0, {
      message: 'errors.network',
      code: 'network_error',
      details: cause instanceof Error ? cause.message : String(cause),
    });
  }
  if (!response.ok) {
    let parsed: { message?: string; code?: string; details?: unknown } = {};
    try {
      parsed = (await response.json()) as typeof parsed;
    } catch {
      // ignore
    }
    throw new ApiError(response.status, {
      message:
        typeof parsed.message === 'string' && parsed.message.length > 0
          ? parsed.message
          : 'errors.generic',
      ...(typeof parsed.code === 'string' ? { code: parsed.code } : {}),
      ...(parsed.details !== undefined ? { details: parsed.details } : {}),
    });
  }
  return response.json() as Promise<T>;
}

export function replaceZonePlaylist(
  composerId: string,
  zoneId: string,
  bindings: ReplaceZoneItemsInput,
): Promise<{ programs: ProgramSummary[] }> {
  return apiPutJson<{ programs: ProgramSummary[] }>(
    `/v1/admin/composers/${composerId}/program/items/${zoneId}/playlist`,
    bindings,
  );
}

// ---------------------------------------------------------------------------
// Phase 2 — Layout editor save (Wave 1C)
// ---------------------------------------------------------------------------
//
// The server endpoint at `/v1/admin/composers/:id/layout` is the territory of
// Phase-2 Wave 1A and may not exist yet on the server we're talking to. The
// editor is responsible for handling a 404 gracefully (toast: "not yet wired")
// while letting all other errors bubble up as the usual save-failure path.

export interface LayoutPayload {
  width_px: number;
  height_px: number;
  zones: Zone[];
}

export function saveComposerLayout(
  composerId: string,
  payload: LayoutPayload,
): Promise<void> {
  return apiPutVoid(`/v1/admin/composers/${composerId}/layout`, payload);
}

// GET counterpart — lets the editor reconstruct a previously-saved
// multi-zone layout on load. A composer that never diverged returns the
// full-screen singleton zone, so this is always safe to seed from.
export interface LayoutResponse {
  width_px: number;
  height_px: number;
  zones: Zone[];
}

export function getComposerLayout(
  composerId: string,
): Promise<LayoutResponse> {
  return apiGet<LayoutResponse>(`/v1/admin/composers/${composerId}/layout`);
}

// ---------------------------------------------------------------------------
// fetchComposers — flat list for the planning library sidebar (T9)
// ---------------------------------------------------------------------------
//
// The sidebar needs all composers without pagination complexity. Fetches up
// to 100 at once — the server caps per_page at 100 (composers.controller.ts:
// z.coerce.number().int().min(1).max(100)); sending 200 returned HTTP 400,
// which left the planning library silently empty. Returns the items array
// directly so callers don't have to unwrap the paginated envelope.

export async function fetchComposers(): Promise<ComposerSummary[]> {
  const response = await listComposers({ per_page: 100 });
  return response.items;
}

// ---------------------------------------------------------------------------
// Audit actions
// ---------------------------------------------------------------------------

const AUDIT_PATHS: Record<ComposerAuditAction, string> = {
  submit_for_review: 'submit-for-review',
  recommend_approve: 'recommend-approve',
  approve: 'approve',
  reject: 'reject',
  revoke: 'revoke',
  reattest: 'reattest',
};

export function runAuditAction(
  composerId: string,
  action: ComposerAuditAction,
  input: AuditActionInput = {},
): Promise<unknown> {
  return apiPost<unknown>(
    `/v1/admin/composers/${composerId}/${AUDIT_PATHS[action]}`,
    input,
  );
}

export function submitForReview(
  id: string,
  input?: AuditActionInput,
): Promise<unknown> {
  return runAuditAction(id, 'submit_for_review', input ?? {});
}

export function recommendApprove(
  id: string,
  input?: AuditActionInput,
): Promise<unknown> {
  return runAuditAction(id, 'recommend_approve', input ?? {});
}

export function approve(
  id: string,
  input?: AuditActionInput,
): Promise<unknown> {
  return runAuditAction(id, 'approve', input ?? {});
}

export function reject(
  id: string,
  input?: AuditActionInput,
): Promise<unknown> {
  return runAuditAction(id, 'reject', input ?? {});
}

export function revoke(
  id: string,
  input?: AuditActionInput,
): Promise<unknown> {
  return runAuditAction(id, 'revoke', input ?? {});
}

export function reattest(
  id: string,
  input?: AuditActionInput,
): Promise<unknown> {
  return runAuditAction(id, 'reattest', input ?? {});
}

// ---------------------------------------------------------------------------
// State-machine helpers for UI gating
// ---------------------------------------------------------------------------

/**
 * Returns the audit actions that are *potentially* visible given the
 * composer's current state. Server is the authority — these gates are UX
 * only (mirrors the resource detail audit drawer behaviour). Permissions
 * are checked in the component using `useAuth().hasPermission`.
 */
export function visibleAuditActionsForState(
  state: ResourceState,
): ReadonlyArray<ComposerAuditAction> {
  switch (state) {
    case 'draft':
      return ['submit_for_review'];
    case 'pending_review':
      return ['recommend_approve', 'reject'];
    case 'recommended':
      return ['approve', 'reject'];
    case 'approved':
      return ['revoke', 'reattest'];
    case 'revoked':
      return [];
    case 'expired':
      return ['reattest'];
    default:
      return [];
  }
}

/**
 * Server-side `composers.{action}` permission name for a given audit action.
 */
export function permissionForAction(action: ComposerAuditAction): string {
  return `composers.${action === 'submit_for_review' ? 'submit' : action === 'recommend_approve' ? 'recommend' : action}`;
}
